import { RbacRole, RolePermission } from "api/rbacRoles";
import {
  PERMISSION_ACTIONS,
  PermissionAction,
  RBAC_RESOURCES,
  RbacResourceDefinition,
} from "config/rbacCatalog";

const ALL_ACTIONS = PERMISSION_ACTIONS.map((entry) => entry.action);

/** Every resource/action pair the server checks; the denominator for "6 of 18". */
export const TOTAL_PERMISSIONS = RBAC_RESOURCES.reduce(
  (sum, resource) => sum + Object.keys(resource.actions).length,
  0
);

export const actionsOf = (resource: RbacResourceDefinition): PermissionAction[] =>
  ALL_ACTIONS.filter((action) => Boolean(resource.actions[action]));

const isSuperName = (name = "") =>
  name
    .toLowerCase()
    .replace(/[\s_-]/g, "")
    .includes("superadmin");

/** Super Admin (by name, legacy flag or a "*" row with every action) can do everything. */
export const isFullAccessRole = (
  role: Pick<RbacRole, "name" | "permissions" | "allPermissionsSelected">
) =>
  Boolean(role.allPermissionsSelected) ||
  isSuperName(role.name) ||
  (role.permissions || []).some(
    (row) => row.resource === "*" && ALL_ACTIONS.every((action) => Boolean(row[action]))
  );

/** API rows (current or legacy `key` shape) → editable rows; unknown flags are kept as they are. */
export const toPermissionRows = (permissions: unknown): RolePermission[] => {
  if (!Array.isArray(permissions)) {
    return [];
  }
  return permissions.flatMap((entry) => {
    const raw = (entry || {}) as Record<string, unknown>;
    const resource = (raw.resource || raw.key) as string | undefined;
    if (!resource) {
      return [];
    }
    const row: RolePermission = { resource };
    ALL_ACTIONS.forEach((action) => {
      if (raw[action] === true) {
        row[action] = true;
      }
    });
    return [row];
  });
};

export const isGranted = (rows: RolePermission[], resource: string, action: PermissionAction) =>
  Boolean(rows.find((row) => row.resource === resource)?.[action]);

/** Sets some actions of one resource; drops the row once nothing is granted. */
export const setGrants = (
  rows: RolePermission[],
  resource: string,
  actions: PermissionAction[],
  value: boolean
): RolePermission[] => {
  const existing = rows.find((row) => row.resource === resource) || { resource };
  const next: RolePermission = { ...existing };
  actions.forEach((action) => {
    if (value) {
      next[action] = true;
    } else {
      delete next[action];
    }
  });
  const others = rows.filter((row) => row.resource !== resource);
  return ALL_ACTIONS.some((action) => next[action]) ? [...others, next] : others;
};

/** Granted pairs among those the server checks. */
export const countGranted = (rows: RolePermission[]) =>
  RBAC_RESOURCES.reduce(
    (sum, resource) =>
      sum +
      actionsOf(resource).filter((action) => isGranted(rows, resource.resource, action)).length,
    0
  );

export interface AreaSummary {
  resource: string;
  label: string;
  /** "all" when every action of the area is granted. */
  scope: "all" | "view" | "partial";
  granted: number;
  total: number;
}

/** Areas the role can touch, in catalog order, for the roles list. */
export const summarizeAccess = (rows: RolePermission[]): AreaSummary[] =>
  RBAC_RESOURCES.flatMap((resource) => {
    const actions = actionsOf(resource);
    const granted = actions.filter((action) => isGranted(rows, resource.resource, action));
    if (granted.length === 0) {
      return [];
    }
    const scope: AreaSummary["scope"] =
      granted.length === actions.length
        ? "all"
        : granted.length === 1 && granted[0] === "read"
        ? "view"
        : "partial";
    return [
      {
        resource: resource.resource,
        label: resource.label,
        scope,
        granted: granted.length,
        total: actions.length,
      },
    ];
  });
